// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Wordloom — static screens (menu, help, about) and the results HTML. The results
// show EVERY player's final grid with the words they wove and their scores (principle
// #9), not just a name and a number.

import { escapeHtml } from './dom';
import { MODE_IDS, modeOf, type Mode } from './modes';
import { scoreGrid, type LineScore } from './score';
import type { GameState } from './game';
import type { PeerResult } from './race';

export const ATTRIB =
  'Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a> · ' +
  '<a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more games, tools &amp; sites</a>';

export function menuHtml(o: { modeId: string; best: number; muted: boolean; dateStr: string }): string {
  const chips = MODE_IDS.map((id) => {
    const m = modeOf(id);
    const on = id === o.modeId;
    return `<button class="chip${on ? ' on' : ''}" data-mode="${id}" aria-pressed="${on}">${escapeHtml(m.name)}</button>`;
  }).join('');
  const mode = modeOf(o.modeId);
  return `
    <section class="screen menu">
      <h1 class="title">Word<span class="title-accent">loom</span></h1>
      <p class="tagline">Weave letters into a grid where every row and column is a word.</p>
      <div class="mode-pick" role="group" aria-label="Choose a mode">${chips}</div>
      <p class="mode-blurb">${escapeHtml(mode.blurb)}</p>
      <p class="best-line">${o.best > 0 ? `Best in ${escapeHtml(mode.name)}: <strong>${o.best}</strong>` : 'No score yet — weave your first grid.'}</p>
      <div class="menu-actions">
        <button class="btn primary" id="playRandom">Play</button>
        <button class="btn" id="playDaily">Daily grid · ${escapeHtml(o.dateStr)}</button>
        <button class="btn" id="playFriends">Play with friends</button>
      </div>
      <div class="menu-links">
        <button class="linkbtn" id="help">How to play</button>
        <button class="linkbtn" id="about">About</button>
        <button class="linkbtn" id="mute">${o.muted ? 'Sound: off' : 'Sound: on'}</button>
      </div>
    </section>`;
}

export function helpHtml(): string {
  return `
    <button class="sheet-close" aria-label="Close">×</button>
    <h2>How to play</h2>
    <p>Letters arrive one at a time — <strong>tap an empty square</strong> to drop each one in.</p>
    <p>When the grid is full, <strong>every row and every column scores its best word</strong> (three
      letters or more). Longer words are worth much more.</p>
    <p>You can’t move a letter once it’s down, so weave carefully: each square is part of a row
      <em>and</em> a column at once. Words light up as you form them — <span class="key sc-col-k">teal</span>
      down a column, <span class="key sc-row-k">amber</span> across a row.</p>
    <p class="muted">In <strong>Sluice</strong> you see the next three letters and place any of them.
      <strong>Deluge</strong> is a bigger 6×6 board where the two diagonals score too.</p>
    <p class="muted">Uses a curated list of everyday words — obscure short words don’t count, so it
      rewards words you actually know. A few rare words may not be accepted.</p>`;
}

export function aboutHtml(): string {
  return `
    <button class="sheet-close" aria-label="Close">×</button>
    <h2>About Wordloom</h2>
    <p>A quiet word-grid game: compose real words in every row and column from letters you can’t
      choose. Play solo, chase the daily grid, or race a friend on the identical letters.</p>
    <p class="muted">No accounts, no cookies, no tracking beyond an anonymous page-view count.
      “Play with friends” connects your devices peer-to-peer over WebRTC; a public signalling relay
      only brokers the first handshake and holds no game data.</p>
    <p class="muted">${ATTRIB}</p>`;
}

/** A mini grid with scoring words lit — reused by solo + multiplayer results. */
export function gridHtml(cells: readonly (string | null)[], mode: Mode): string {
  const gs = scoreGrid(cells, mode);
  const cellsHtml = cells
    .map((ch, i) => {
      const kinds = gs.cellKinds.get(i);
      const cls = [
        'r-cell',
        ch != null ? 'filled' : 'empty',
        kinds?.has('row') ? 'sc-row' : '',
        kinds?.has('col') ? 'sc-col' : '',
        kinds?.has('diag') ? 'sc-diag' : '',
        kinds ? 'scoring' : '',
      ]
        .filter(Boolean)
        .join(' ');
      return `<span class="${cls}">${ch ? escapeHtml(ch) : ''}</span>`;
    })
    .join('');
  return `<div class="r-grid" style="--n:${mode.size}">${cellsHtml}</div>`;
}

/** The list of words scored, best first. */
function wordsHtml(cells: readonly (string | null)[], mode: Mode): string {
  const gs = scoreGrid(cells, mode);
  const words = gs.lines
    .filter((l): l is LineScore & { best: NonNullable<LineScore['best']> } => l.best != null)
    .sort((a, b) => b.best.score - a.best.score);
  if (!words.length) return `<p class="r-words muted">No words this time.</p>`;
  return `<ul class="r-words">${words
    .map((l) => `<li><span class="rw-word">${escapeHtml(l.best.word)}</span><span class="rw-pts">${l.best.score}</span></li>`)
    .join('')}</ul>`;
}

export function soloResultsHtml(st: GameState, mode: Mode, best: number, daily: boolean): string {
  const total = scoreGrid(st.cells, mode).total;
  const isBest = total >= best && total > 0;
  return `
    <section class="screen results">
      <h2 class="r-title">${isBest ? 'New best!' : 'Woven'}</h2>
      <div class="r-score"><span class="r-num">${total}</span><span class="r-sub">${escapeHtml(mode.name)}${daily ? ' · daily' : ''}</span></div>
      <p class="r-best">Best: <strong>${Math.max(best, total)}</strong></p>
      ${gridHtml(st.cells, mode)}
      ${wordsHtml(st.cells, mode)}
    </section>`;
}

export function multiResultsHtml(
  ranked: PeerResult[],
  selfSeat: number | null,
  mode: Mode,
  tally: number[],
): string {
  const rows = ranked
    .map((r, i) => {
      const you = r.seat === selfSeat;
      const wins = tally[r.seat] ?? 0;
      const grid = r.cells ? gridHtml(r.cells, mode) : `<div class="r-grid pending" style="--n:${mode.size}"></div>`;
      const status = r.left
        ? '<span class="r-left">left</span>'
        : r.over
          ? ''
          : '<span class="r-playing">weaving…</span>';
      return `
        <div class="r-player${you ? ' you' : ''}${i === 0 && !r.left && r.over ? ' lead' : ''}">
          <div class="r-head">
            <span class="r-rank">${i + 1}</span>
            <span class="r-name">${escapeHtml(r.name)}${you ? ' (you)' : ''}</span>
            ${wins > 0 ? `<span class="r-tally" title="rounds won">🏆 ${wins}</span>` : ''}
            <span class="r-pscore">${r.score}</span> ${status}
          </div>
          ${grid}
          ${r.cells ? wordsHtml(r.cells, mode) : ''}
        </div>`;
    })
    .join('');
  return `<h2 class="r-title">Results</h2><div class="r-players">${rows}</div>`;
}
